/**
 * One row in the key list: the label, the non-secret prefix, the scopes it carries and
 * when it was last used. Revoking is a one-way door, so it asks first.
 */
import { Trash2 } from "lucide-react";
import { Button } from "@wa/ui";
import { apiKeysApi, type ApiKey } from "../../../lib/apiKeys";

interface Props {
  apiKey: ApiKey;
  t: (key: string) => string;
  onChange: () => void;
}

export function KeyRow({ apiKey, t, onChange }: Props) {
  const revoked = !!apiKey.revokedAt;

  async function revoke() {
    if (!window.confirm(t("apiKeys.confirmRevoke"))) return;
    await apiKeysApi.revoke(apiKey.id);
    onChange();
  }

  return (
    <div className={`flex items-center gap-3 rounded-md border p-3 ${revoked ? "opacity-50" : ""}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{apiKey.name}</span>
          <code className="text-xs text-muted-foreground">{apiKey.prefix}...</code>
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {apiKey.scopes.map((s) => (
            <span key={s} className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono">
              {s}
            </span>
          ))}
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {apiKey.lastUsedAt
            ? `${t("apiKeys.lastUsed")} ${new Date(apiKey.lastUsedAt).toLocaleDateString()}`
            : t("apiKeys.neverUsed")}
        </p>
      </div>
      {!revoked && (
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => void revoke()}>
          <Trash2 className="size-3.5" />
        </Button>
      )}
    </div>
  );
}
